import { useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { AlertIncident, IncidentState } from "@/api/alerts-api"
import type { TFunction } from "i18next"

import { resolveIncident, unresolveIncident } from "@/api/alerts-api"
import { Bell, Check, ChevronDown, ChevronUp, RefreshCw, RotateCcw } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"

import { SeverityIcon, StatusIndicator } from "./alert-status-indicator"
import { formatTimeAgo, getAlertStatus } from "./alert-status.utils"
import { useAdminRecentAlertsQuery } from "../../hooks/admin-queries"
import { adminQueryKeys } from "../../hooks/admin-query-keys"

type Props = {
  days?: number
}

const COLLAPSED_INCIDENT_COUNT = 5

function IncidentRow({
  incident,
  onResolve,
  onUnresolve,
  isResolving,
  t,
}: {
  incident: AlertIncident
  onResolve?: (incident: AlertIncident) => void
  onUnresolve?: (alertKey: string) => void
  isResolving?: boolean
  t: TFunction
}) {
  const isResolved = incident.state === "resolved"
  const isStale = incident.state === "stale"

  return (
    <div
      className={`hover:bg-muted/50 flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors ${
        isStale ? "opacity-60" : ""
      } ${isResolved ? "bg-muted/30" : ""}`}
    >
      <SeverityIcon severity={incident.severity} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium" title={incident.subject}>
            {incident.subject}
          </span>
          {isResolved && <Check className="text-success size-3.5" />}
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
          <span>{formatTimeAgo(incident.last_occurrence)}</span>
          <span>•</span>
          <code className="bg-muted rounded px-1">{incident.alert_type}</code>
          {incident.delivery_mode && incident.delivery_mode !== "sent" && (
            <>
              <span>•</span>
              <Badge className="text-xs" variant="outline">
                {incident.delivery_mode === "dry_run" ? "Dry Run" : "Log Only"}
              </Badge>
            </>
          )}
        </div>
        {isResolved && incident.resolved_by && (
          <div className="text-muted-foreground mt-1 text-xs">
            {t("adminDashboard.alerts.resolvedBy", "Resolved by {{user}}", {
              user: incident.resolved_by,
            })}
            {incident.resolved_at && ` • ${formatTimeAgo(incident.resolved_at)}`}
            {incident.resolved_note && (
              <div className="mt-0.5 italic">"{incident.resolved_note}"</div>
            )}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {incident.occurrence_count > 1 && (
          <Badge className="text-xs" variant="secondary">
            {incident.occurrence_count.toLocaleString()}x
          </Badge>
        )}
        {!isResolved && onResolve && (
          <Button
            className="h-7 px-2 text-xs"
            disabled={isResolving}
            size="sm"
            variant="ghost"
            onClick={() => onResolve(incident)}
          >
            <Check className="mr-1 size-3" />
            {t("adminDashboard.alerts.resolve", "Resolve")}
          </Button>
        )}
        {isResolved && onUnresolve && (
          <Button
            className="h-7 px-2 text-xs"
            disabled={isResolving}
            size="sm"
            variant="ghost"
            onClick={() => onUnresolve(incident.alert_key)}
          >
            <RotateCcw className="mr-1 size-3" />
            {t("adminDashboard.alerts.reopen", "Re-open")}
          </Button>
        )}
      </div>
    </div>
  )
}

function IncidentList({
  incidents,
  emptyMessage,
  onResolve,
  onUnresolve,
  isResolving,
  t,
}: {
  incidents: AlertIncident[]
  emptyMessage: string
  onResolve?: (incident: AlertIncident) => void
  onUnresolve?: (alertKey: string) => void
  isResolving?: boolean
  t: TFunction
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasMore = incidents.length > COLLAPSED_INCIDENT_COUNT
  const displayedIncidents = isExpanded ? incidents : incidents.slice(0, COLLAPSED_INCIDENT_COUNT)

  if (incidents.length === 0) {
    return <div className="text-muted-foreground py-4 text-center text-sm">{emptyMessage}</div>
  }

  return (
    <div className="space-y-2">
      {displayedIncidents.map((incident) => (
        <IncidentRow
          key={incident.alert_key}
          incident={incident}
          isResolving={isResolving}
          t={t}
          onResolve={onResolve}
          onUnresolve={onUnresolve}
        />
      ))}
      {hasMore && (
        <Button
          className="text-muted-foreground hover:bg-muted hover:text-foreground w-full"
          size="sm"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="mr-1 size-4" />
              {t("adminDashboard.alerts.showLess", "Show less")}
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 size-4" />
              {t("adminDashboard.alerts.showMoreIncidents", "Show all {{count}} incidents", {
                count: incidents.length,
              })}
            </>
          )}
        </Button>
      )}
    </div>
  )
}

export function RecentAlertsCard({ days = 7 }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useAdminRecentAlertsQuery(days)
  const [activeTab, setActiveTab] = useState<IncidentState>("active")
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [selectedIncident, setSelectedIncident] = useState<AlertIncident | null>(null)
  const [resolveNote, setResolveNote] = useState("")

  // Get incidents by state
  const activeIncidents = data?.active || []
  const staleIncidents = data?.stale || []
  const resolvedIncidents = data?.resolved || []
  const counts = data?.counts || { active: 0, stale: 0, resolved: 0 }
  const totalOccurrences = data?.total_occurrences || 0

  // Get severity counts from active incidents for status indicator
  const severityCounts = data?.severity_counts || {}
  const criticalCount = severityCounts.critical || 0
  const warningCount = severityCounts.warning || 0

  const alertStatus = getAlertStatus(criticalCount, warningCount)

  // Mutations
  const resolveMutation = useMutation({
    mutationFn: ({ alertKey, note }: { alertKey: string; note?: string }) =>
      resolveIncident(alertKey, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.alertRecent(days) })
      setResolveDialogOpen(false)
      setSelectedIncident(null)
      setResolveNote("")
    },
  })

  const unresolveMutation = useMutation({
    mutationFn: (alertKey: string) => unresolveIncident(alertKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.alertRecent(days) })
    },
  })

  const handleResolveClick = (incident: AlertIncident) => {
    setSelectedIncident(incident)
    setResolveNote("")
    setResolveDialogOpen(true)
  }

  const handleResolveConfirm = () => {
    if (selectedIncident) {
      resolveMutation.mutate({
        alertKey: selectedIncident.alert_key,
        note: resolveNote || undefined,
      })
    }
  }

  const handleUnresolve = (alertKey: string) => {
    unresolveMutation.mutate(alertKey)
  }

  const isResolving = resolveMutation.isPending || unresolveMutation.isPending
  const incidentsUnavailable = isError || Boolean(data?.error) || (!isLoading && !data)

  return (
    <>
      <Card className="rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="size-5" />
              <CardTitle className="text-lg">
                {t("adminDashboard.alerts.recentIncidents", "Recent Incidents")}
              </CardTitle>
            </div>
            <Button size="icon" variant="ghost" onClick={() => refetch()}>
              <RefreshCw className={`size-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <CardDescription>
            {t("adminDashboard.alerts.last7Days", "Last {{days}} days", { days })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="text-muted-foreground size-6 animate-spin" />
            </div>
          )}
          {!isLoading && incidentsUnavailable && (
            <div className="text-muted-foreground py-6 text-sm" role="alert">
              {t("adminDashboard.alerts.summaryUnavailable", "Incident status unavailable")}
            </div>
          )}
          {!isLoading && !incidentsUnavailable && (
            <div className="space-y-4">
              {/* Status Header */}
              <StatusIndicator
                criticalCount={criticalCount}
                status={alertStatus}
                t={t}
                warningCount={warningCount}
              />

              {/* Tabs for incident states */}
              <Tabs
                className="w-full"
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as IncidentState)}
              >
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger className="text-xs" value="active">
                    {t("adminDashboard.alerts.tabActive", "Active")} ({counts.active})
                  </TabsTrigger>
                  <TabsTrigger className="text-xs" value="stale">
                    {t("adminDashboard.alerts.tabStale", "Stale")} ({counts.stale})
                  </TabsTrigger>
                  <TabsTrigger className="text-xs" value="resolved">
                    {t("adminDashboard.alerts.tabResolved", "Resolved")} ({counts.resolved})
                  </TabsTrigger>
                </TabsList>

                <TabsContent className="mt-3" value="active">
                  <IncidentList
                    incidents={activeIncidents}
                    isResolving={isResolving}
                    t={t}
                    emptyMessage={t(
                      "adminDashboard.alerts.noActiveIncidents",
                      "No active incidents"
                    )}
                    onResolve={handleResolveClick}
                  />
                </TabsContent>

                <TabsContent className="mt-3" value="stale">
                  <IncidentList
                    emptyMessage={t("adminDashboard.alerts.noStaleIncidents", "No stale incidents")}
                    incidents={staleIncidents}
                    isResolving={isResolving}
                    t={t}
                    onResolve={handleResolveClick}
                  />
                </TabsContent>

                <TabsContent className="mt-3" value="resolved">
                  <IncidentList
                    incidents={resolvedIncidents}
                    isResolving={isResolving}
                    t={t}
                    emptyMessage={t(
                      "adminDashboard.alerts.noResolvedIncidents",
                      "No resolved incidents"
                    )}
                    onUnresolve={handleUnresolve}
                  />
                </TabsContent>
              </Tabs>

              {/* Total count */}
              {totalOccurrences > 0 && (
                <div className="text-muted-foreground text-center text-sm">
                  {t(
                    "adminDashboard.alerts.incidentSummary",
                    "{{incidents}} incidents ({{occurrences}} total events)",
                    {
                      incidents: counts.active + counts.stale + counts.resolved,
                      occurrences: totalOccurrences.toLocaleString(),
                    }
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.alerts.resolveDialogTitle", "Resolve Incident")}
            </DialogTitle>
            <DialogDescription>
              {selectedIncident?.subject}
              {selectedIncident && selectedIncident.occurrence_count > 1 && (
                <span className="ml-1">
                  ({selectedIncident.occurrence_count.toLocaleString()}{" "}
                  {t("adminDashboard.alerts.occurrences", "occurrences")})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">
              {t("adminDashboard.alerts.resolveNoteLabel", "Comment (optional)")}
            </label>
            <Textarea
              className="mt-2"
              rows={3}
              value={resolveNote}
              placeholder={t(
                "adminDashboard.alerts.resolveNotePlaceholder",
                "Add a note about how this was resolved..."
              )}
              onChange={(e) => setResolveNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button disabled={resolveMutation.isPending} onClick={handleResolveConfirm}>
              {resolveMutation.isPending ? (
                <RefreshCw className="mr-2 size-4 animate-spin" />
              ) : (
                <Check className="mr-2 size-4" />
              )}
              {t("adminDashboard.alerts.resolve", "Resolve")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
